import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { OcaDeliveriesActions } from './ocaDeliveries.actions';
import { IOcaShipping, Pagination } from '../../interfaces/deliveries.interface';
import { FileUploadService, UploadProgress } from '../../services/file-upload/file-upload.service';
import { OcaDeliveriesService } from '../../services/ocaDeliveries/ocaDeliveries.service';

export interface OcaDeliveriesStateModel {
  deliveries: Pagination<IOcaShipping>;
  loading: boolean;
  delivery: IOcaShipping | null;
  uploadProgress: UploadProgress | null;
  uploading: boolean;
  uploadError: string | null;
}

@State<OcaDeliveriesStateModel>({
  name: 'ocaDeliveries',
  defaults: {
    deliveries: {
      items: [],
      page: 0,
      pageSize: 0,
      total: 0,
    },
    loading: false,
    delivery: null,
    uploadProgress: null,
    uploading: false,
    uploadError: null,
  },
})

@Injectable()
export class OcaDeliveriesState {
  constructor(
    private ocaDeliveriesService: OcaDeliveriesService,
    private fileUploadService: FileUploadService
  ) {}

  @Selector()
  static getDeliveries(state: OcaDeliveriesStateModel): Pagination<IOcaShipping>{
    return state.deliveries;
  }

  @Selector()
  static isLoading(state: OcaDeliveriesStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getDelivery(state: OcaDeliveriesStateModel): IOcaShipping | null {
    return state.delivery;
  }

  @Selector()
  static isUploading(state: OcaDeliveriesStateModel): boolean {
    return state.uploading;
  }

  @Selector()
  static getUploadProgress(state: OcaDeliveriesStateModel): UploadProgress | null {
    return state.uploadProgress;
  }

  @Selector()
  static getUploadError(state: OcaDeliveriesStateModel): string | null {
    return state.uploadError;
  }

  @Action(OcaDeliveriesActions.GetOcaDeliveries)
  getDeliveries(ctx: StateContext<OcaDeliveriesStateModel>, action: OcaDeliveriesActions.GetOcaDeliveries) {
    ctx.patchState({ loading: true });

    console.log('llego al state')

    return this.ocaDeliveriesService.getOcaDeliveries(
      action.fechaDesde, 
      action.page, 
      action.pageSize, 
      action.estado,
      action.q,
      action.devoluciones,
      action.estadoInterno,
    ).pipe(
      tap({
        next: (response) => {
          console.log('Response in state:', response);
          console.log('Response data:', response.data);
          ctx.patchState({
            deliveries: response.data,
            loading: false,
          });
        },
        error: (error) => {
          console.error('Error in state:', error);
          ctx.patchState({
            loading: false,
          });
        },
      })
    );
  }

  @Action(OcaDeliveriesActions.SetOcaDeliveries)
  setDeliveries(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.SetOcaDeliveries
  ) {
    const state = ctx.getState();
    ctx.patchState({
      deliveries: action.deliveries,
    });
  }

  @Action(OcaDeliveriesActions.GetOcaDeliveryById)
  getDeliveryById(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.GetOcaDeliveryById
  ) {
    ctx.patchState({
      loading: true,
      delivery: null,
      uploadError: null,
      uploading: false,
      uploadProgress: null,
    });
    return this.ocaDeliveriesService.getOcaDeliveryByTrackingNumber(action.trackingNumber).pipe(
      tap({
        next: (response) => {
          ctx.patchState({
            delivery: response.data,
            loading: false,
          });
        },
        error: () => {
          ctx.patchState({
            loading: false,
          });
        },
      })
    );
  }

  @Action(OcaDeliveriesActions.GetOcaReturnById)
  getReturnById(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.GetOcaReturnById
  ) {
    ctx.patchState({
      loading: true,
      delivery: null,
      uploadError: null,
      uploading: false,
      uploadProgress: null,
    });
    return this.ocaDeliveriesService.getOcaReturnByTrackingNumber(action.trackingNumber).pipe(
      tap({
        next: (response) => {
          ctx.patchState({
            delivery: response.data,
            loading: false,
          });
        },
        error: () => {
          ctx.patchState({
            loading: false,
          });
        },
      })
    );
  }

  @Action(OcaDeliveriesActions.UploadOcaFile)
  uploadOcaFile(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFile
  ) {
    ctx.patchState({ 
      uploading: true, 
      uploadError: null,
      uploadProgress: null
    });

    return this.ocaDeliveriesService.uploadFile(
      action.shippingId, 
      action.receiptId,
      action.file
    ).pipe(
      tap(event => {
        const progress = this.fileUploadService.getUploadProgress(event);
        if (progress) {
          ctx.dispatch(new OcaDeliveriesActions.UploadOcaFileProgress(progress));
        }

        if (this.fileUploadService.isUploadComplete(event)) {
          const response = event as any;
          ctx.dispatch(new OcaDeliveriesActions.UploadOcaFileSuccess(response.body));
          // Refrescar datos del delivery/return
          if (action.isReturn) {
            ctx.dispatch(new OcaDeliveriesActions.GetOcaReturnById(action.trackingNumber));
          } else {
            ctx.dispatch(new OcaDeliveriesActions.GetOcaDeliveryById(action.trackingNumber));
          }
        }
      }),
      catchError(error => {
        ctx.dispatch(new OcaDeliveriesActions.UploadOcaFileError(error.message));
        return throwError(() => error);
      })
    );
  }

  @Action(OcaDeliveriesActions.UploadOcaFileProgress)
  uploadOcaFileProgress(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFileProgress
  ) {
    ctx.patchState({ uploadProgress: action.progress });
  }

  @Action(OcaDeliveriesActions.UploadOcaFileSuccess)
  uploadOcaFileSuccess(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFileSuccess
  ) {
    ctx.patchState({ 
      uploading: false, 
      uploadProgress: null,
      uploadError: null 
    });
  }

  @Action(OcaDeliveriesActions.UploadOcaFileError)
  uploadOcaFileError(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFileError
  ) {
    ctx.patchState({ 
      uploading: false, 
      uploadProgress: null,
      uploadError: action.error 
    });
  }

  @Action(OcaDeliveriesActions.UploadOcaFiles)
  uploadOcaFiles(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFiles
  ) {
    ctx.patchState({ 
      uploading: true, 
      uploadError: null,
      uploadProgress: null
    });

    return this.ocaDeliveriesService.uploadFiles(
      action.shippingId, 
      action.receiptId,
      action.files
    ).pipe(
      tap(event => {
        const progress = this.fileUploadService.getUploadProgress(event);
        if (progress) {
          ctx.dispatch(new OcaDeliveriesActions.UploadOcaFilesProgress(progress));
        }

        if (this.fileUploadService.isUploadComplete(event)) {
          const response = event as any;
          ctx.dispatch(new OcaDeliveriesActions.UploadOcaFilesSuccess(response.body));
          // Refrescar datos del delivery/return
          if (action.isReturn) {
            ctx.dispatch(new OcaDeliveriesActions.GetOcaReturnById(action.trackingNumber));
          } else {
            ctx.dispatch(new OcaDeliveriesActions.GetOcaDeliveryById(action.trackingNumber));
          }
        }
      }),
      catchError(error => {
        ctx.dispatch(new OcaDeliveriesActions.UploadOcaFilesError(error.message));
        return throwError(() => error);
      })
    );
  }

  @Action(OcaDeliveriesActions.UploadOcaFilesProgress)
  uploadOcaFilesProgress(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFilesProgress
  ) {
    ctx.patchState({ uploadProgress: action.progress });
  }

  @Action(OcaDeliveriesActions.UploadOcaFilesSuccess)
  uploadOcaFilesSuccess(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFilesSuccess
  ) {
    ctx.patchState({ 
      uploading: false, 
      uploadProgress: null,
      uploadError: null 
    });
  }

  @Action(OcaDeliveriesActions.UploadOcaFilesError)
  uploadOcaFilesError(
    ctx: StateContext<OcaDeliveriesStateModel>,
    action: OcaDeliveriesActions.UploadOcaFilesError
  ) {
    ctx.patchState({ 
      uploading: false, 
      uploadProgress: null,
      uploadError: action.error 
    });
  }
}